import { get as getCookie, set as setCookie } from 'js-cookie';
import { throttle } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useHasChanged } from '~/hooks/useHasChanged';
import { fetchFromOldApi } from '~/services/Http';
import {
  getNumberOrUndefined,
  redirectToLogOut,
  redirectToOrigin,
} from '~/utils/helper';

export const LAST_USER_INTERACTION_TIME_COOKIE_NAME =
  'last-user-interaction-time';
export const CHECK_TIMEOUT_INTERVAL = 60 * 1000;

export interface UseSessionMonitorOutput {
  minutesToExpiration: string;
  continueSession: () => void;
}

export const continueUserSession = () => {
  setCookie(LAST_USER_INTERACTION_TIME_COOKIE_NAME, Date.now().toString());
  // API Call that will make sure BE refreshes the session cookie
  fetchFromOldApi('/json/status').catch(() => {
    redirectToOrigin('Session expired');
  });
};

export const useSessionInitialize = (props: { isAuthenticated: boolean }) => {
  const { isAuthenticated } = props;

  const hasAuthenticatedStatusChanged = useHasChanged(isAuthenticated);
  useEffect(() => {
    const lastUserInteraction = getNumberOrUndefined(
      getCookie(LAST_USER_INTERACTION_TIME_COOKIE_NAME),
    );

    if (
      isAuthenticated &&
      (hasAuthenticatedStatusChanged || !lastUserInteraction)
    ) {
      continueUserSession();
    }
  }, [isAuthenticated, hasAuthenticatedStatusChanged]);
};

export const useSessionMonitor = (): UseSessionMonitorOutput => {
  const checkTimeoutInterval = useRef<number>();
  const [minutesToExpiration, setMinutesToExpiration] = useState('');

  const continueSession = useCallback(() => {
    setMinutesToExpiration('');
    continueUserSession();
  }, [setMinutesToExpiration]);

  const checkTimeout = useCallback(() => {
    const lastUserInteractionTime =
      getCookie(LAST_USER_INTERACTION_TIME_COOKIE_NAME) || 0;
    const iIdleMinutes = Math.round(
      (Date.now() - +lastUserInteractionTime) / CHECK_TIMEOUT_INTERVAL,
    );

    // Session expiration
    if (iIdleMinutes >= 29) {
      window.clearInterval(checkTimeoutInterval.current);
      setMinutesToExpiration('');
      redirectToLogOut('Session timeout');
      // Session will expire in >>> 2 minutes
    } else if (iIdleMinutes === 27) {
      setMinutesToExpiration('2');
      // Session will expire in >>> 5 minutes
    } else if (iIdleMinutes === 24) {
      setMinutesToExpiration('5');
    }
  }, [setMinutesToExpiration]);

  const isDialogOpen = !!minutesToExpiration;
  useEffect(() => {
    if (!isDialogOpen) {
      const events = [
        'load',
        'mousemove',
        'mousedown',
        'click',
        'scroll',
        'keypress',
      ];

      const throttledContinueSession = throttle(
        continueSession,
        CHECK_TIMEOUT_INTERVAL,
      );

      for (const i in events) {
        window.addEventListener(events[i], throttledContinueSession);
      }

      return () => {
        for (const i in events) {
          window.removeEventListener(events[i], throttledContinueSession);
        }
      };
    }
  }, [continueSession, minutesToExpiration, isDialogOpen]);

  useEffect(() => {
    checkTimeoutInterval.current = window.setInterval(
      checkTimeout,
      CHECK_TIMEOUT_INTERVAL,
    );
    return () => {
      window.clearInterval(checkTimeoutInterval.current);
    };
  }, [checkTimeout]);

  return {
    minutesToExpiration,
    continueSession,
  };
};
